import { Component, OnInit, ViewChild } from '@angular/core';
import { UserInfo, LogStatus, LogError, Metadata, RunView } from '@memberjunction/core';
import { RoleEntity, UserEntity, UserRoleEntity } from '@memberjunction/core-entities';
import { ActiveUserService } from 'src/app/services/active-user.service';
import { ImpersonationService } from 'src/app/services/impersonation.service';
import { ImpersonationPermissionService } from 'src/app/services/impersonation-permission.service';
import { DataFetchService } from 'src/app/services/data-fetch.service';
import { DataGridColumn } from 'src/app/utils/interfaces';
import { EntityNames, SYSTEM_ROLES } from 'src/app/utils/constants';
import { AcsDataGridComponent } from '../shared/acs-data-grid/acs-data-grid';
import { MembershipRosterEntity } from 'mj_generatedentities';

// Define interface for formatted user data
interface UserTableData {
  UserID: string;
  Name: string;
  Email: string;
}

@Component({
  selector: 'acs-admin-users',
  templateUrl: './admin-users.html',
  styleUrls: ['../../../styles/_page-layout.scss', './admin-users-specific.scss']
})
export class AdminUsersComponent implements OnInit {
  @ViewChild('usersGrid') usersGrid!: AcsDataGridComponent;
  
  private metadata = new Metadata();
  
  isLoading = false;
  error: string | null = null;
  currentUser!: UserInfo;

  // Grid Configuration - client-side data
  gridColumns: DataGridColumn[] = [
    { key: 'Name', label: 'Name', sortable: true, filterable: true, width: '300px', type: 'text' },
    { key: 'Email', label: 'Email', sortable: true, filterable: true, width: '350px', type: 'text' },
    {
      key: 'actions',
      label: 'Impersonate',
      sortable: false,
      filterable: false,
      width: '120px',
      align: 'left',
      type: 'actions',
      actions: [
        {
          label: 'Impersonate User',
          icon: '👤',
          handler: (item: UserTableData) => this.onImpersonateUser(item),
          disabled: (item: UserTableData) => this.isImpersonationDisabled(item)
        }
      ]
    }
  ];
  
  // Table data - formatted for display
  tableData: UserTableData[] = [];
  
  // Impersonation Modal State
  showImpersonationModal = false;
  impersonationTarget: UserTableData | null = null;
  impersonationReason = '';
  impersonationDuration = 60; // Default 60 minutes
  isStartingImpersonation = false;
  
  constructor(
    private activeUserService: ActiveUserService,
    private impersonationService: ImpersonationService,
    private impersonationPermissionService: ImpersonationPermissionService,
    private dataFetchService: DataFetchService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  async initializeComponent(): Promise<void> {
    this.isLoading = true;
    
    try {
      // Wait for active user to be ready
      await this.activeUserService.waitForActiveUser();
      this.currentUser = this.activeUserService.getActiveUser()!;
      
      await this.loadUserData();
    } catch (error) {
      LogError('Error initializing component:', undefined, error);
      this.error = 'Failed to load initial data';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadUserData(): Promise<void> {
    try {

      // Load all users
      const usersResult = await this.dataFetchService.fetchWithParams<UserInfo>({
        EntityName: EntityNames.Users,
        OrderBy: 'Name',
        skipActiveFilter: true,
        ResultType: 'simple'
      });

      if (usersResult.success) {
        const users = usersResult.data;
        
        // Format the data for display
        const formattedData = await this.formatUserData(users);
        
        // Filter out system users and sort by name
        this.tableData = formattedData
          .filter(data => data.Name !== 'System')
          .sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));
      }
    } catch (error) {
      LogError('Error loading user data:', undefined, error);
      this.error = 'Failed to load user data';
    }
  }

  private async formatUserData(users: UserInfo[]): Promise<UserTableData[]> {
    const formattedData: UserTableData[] = [];

    for (const user of users) {
      const displayName = user.FirstLast || user.Name || user.Email;

      formattedData.push({
        UserID: user.ID,
        Name: displayName,
        Email: user.Email || ''
      });
    }

    return formattedData;
  }

  async onImpersonateUser(userData: UserTableData): Promise<void> {
    try {
      LogStatus(`AdminUsers: Checking impersonation permissions for User_ID: ${userData.UserID}`);

      // ✅ NEW: Pass User_ID directly (no need for PersonId lookup first!)
      // Permission service will check ExtendedUsers first, then fallback if needed
      const canImpersonate = await this.impersonationPermissionService.canImpersonate(
        userData.UserID,      // PREFERRED: User_ID for fast ExtendedUsers lookup
        userData.Email,       // FALLBACK: Email if ExtendedUsers not found
        undefined             // PersonId will be fetched if needed
      );

      if (!canImpersonate.isAllowed) {
        this.error = `Cannot impersonate user: ${canImpersonate.reason}`;
        return;
      }

      // Show confirmation modal
      this.impersonationTarget = userData;
      this.impersonationReason = '';
      this.impersonationDuration = 60;
      this.showImpersonationModal = true;
    } catch (error) {
      LogError('Error checking impersonation permissions:', undefined, error);
      this.error = 'Failed to check impersonation permissions';
    }
  }

  // Impersonation Methods
  async startImpersonation(): Promise<void> {
    if (!this.impersonationTarget) return;

    this.isStartingImpersonation = true;
    try {
      const targetUser = await this.getUserInfoById(this.impersonationTarget.UserID);
      if (!targetUser) {
        this.error = 'Target user not found';
        return;
      }

      const durationMs = this.impersonationDuration * 60 * 1000; // Convert minutes to milliseconds
      const reason = this.impersonationReason?.trim() || null; // Allow null for optional reason
      const result = await this.impersonationService.startImpersonation(
        this.currentUser,
        targetUser,
        reason || 'Admin impersonation',
        durationMs
      );

      if (result.success) {
        LogStatus(`Successfully started impersonating ${this.impersonationTarget.Name}`);
        this.closeImpersonationModal();
        // Refresh the page to reflect the new impersonation state
        window.location.reload();
      } else {
        this.error = `Failed to start impersonation: ${result.message}`;
      }
    } catch (error) {
      LogError('Error starting impersonation:', undefined, error);
      this.error = 'Failed to start impersonation session';
    } finally {
      this.isStartingImpersonation = false;
    }
  }

  closeImpersonationModal(): void {
    this.showImpersonationModal = false;
    this.impersonationTarget = null;
    this.impersonationReason = '';
    this.impersonationDuration = 60;
  }

  isImpersonationDisabled(userData: UserTableData): boolean {
    // Disable if it's the current user
    if (userData.UserID === this.currentUser.ID) {
      return true;
    }

    // Disable if already impersonating
    if (this.activeUserService.isImpersonating()) {
      return true;
    }

    return false;
  }


  private async getUserInfoById(userId: string): Promise<UserInfo | null> {
    try {
      // Use DataFetchService to get user with proper UserInfo structure
      const userResult = await this.dataFetchService.fetchWithParams<UserInfo>({
        EntityName: EntityNames.Users,
        ExtraFilter: `ID = '${userId}'`,
        ResultType: 'simple',
        skipActiveFilter: true
      });

      if (userResult.success && userResult.data.length > 0) {
        const userInfo = userResult.data[0];

        // Load user roles separately and merge them
        const userRoleResult = await this.dataFetchService.fetchWithParams<UserRoleEntity>({
          EntityName: EntityNames.UserRoles,
          ExtraFilter: `UserID = '${userId}'`,
          skipActiveFilter: true,
          ResultType: 'simple'
        });

        // Create a proper UserInfo object with all required properties
        // Using Object.assign to ensure all properties are copied
        const enrichedUserInfo: UserInfo = Object.assign({}, userInfo, {
          // Ensure critical fields are present
          ID: userInfo.ID,
          Name: userInfo.Name,
          Email: userInfo.Email,
          FirstLast: userInfo.FirstLast,
          FirstName: userInfo.FirstName,
          LastName: userInfo.LastName,
          Type: userInfo.Type || 'User',
          IsActive: userInfo.IsActive !== undefined ? userInfo.IsActive : true,
          // Add UserRoles
          UserRoles: userRoleResult.success && userRoleResult.data.length > 0 
            ? userRoleResult.data.map((ur: any) => ({
                UserID: ur.UserID,
                RoleID: ur.RoleID,
                Role: ur.Role
              }))
            : [] // Ensure UserRoles is always an array, even if empty
        });
        
        // Verify the ID is properly set
        if (!enrichedUserInfo.ID) {
          console.error('Warning: UserInfo ID is not set for user:', enrichedUserInfo);
        }
        
        return enrichedUserInfo;
      }
    } catch (error) {
      LogError('Error loading user by ID:', undefined, error);
    }
    
    return null;
  }

}